import {
  AllocationMode,
  IAllocationActivity,
  IAllocationCostCenter,
  IAllocationLineItem,
  IAllocationLink,
  IAllocationSummary,
  IAllocationValidationIssue,
} from './budgetallocation.models';

import { postBiz, json, rows } from '../core/coreapi';

export const normalizeAllocationCostCenter = (r: any): IAllocationCostCenter => ({
  recid: r.recid ?? r.rid ?? r.id,
  shortcode: r.shortcode ?? r.shc ?? r.costcentercode ?? r.ccc ?? '',
  recname: r.recname ?? r.nam ?? r.costcentername ?? r.ccn ?? '',
  activity_count: Number(r.activity_count ?? r.activities ?? r.act ?? 0),
  allocated_amount: Number(r.allocated_amount ?? r.amount ?? r.amt ?? 0),
  raw: r,
} as any);

export const normalizeAllocationActivity = (r: any): IAllocationActivity => ({
  id: r.id ?? r.recid ?? r.rid,
  recid: r.recid ?? r.rid ?? r.id,
  shortcode: r.shortcode ?? r.shc ?? r.activitycode ?? r.code ?? '',
  recname: r.recname ?? r.nam ?? r.activityname ?? r.name ?? '',
  description: r.description ?? r.dsc ?? '',
  ccid: r.ccid ?? r.costcenterid ?? r.eci ?? null,
  costcenter_code: r.costcenter_code ?? r.costcentercode ?? r.ccc ?? '',
  costcenter_name: r.costcenter_name ?? r.costcentername ?? r.ccn ?? '',
  driver_count: Number(r.driver_count ?? r.drivers ?? 0),
  allocation_count: Number(r.allocation_count ?? r.links ?? 0),
  allocated_amount: Number(r.allocated_amount ?? r.amount ?? r.amt ?? 0),
  allocation_status: r.allocation_status ?? r.statusname ?? r.stn ?? '',
  statecode: r.statecode ?? r.stc ?? '',
  statename: r.statename ?? r.stn ?? '',
  raw: r,
} as any);

export const normalizeAllocationLineItem = (r: any): IAllocationLineItem => ({
  id: r.id ?? r.recid ?? r.rid,
  recid: r.recid ?? r.rid ?? r.id,
  shortcode: r.shortcode ?? r.shc ?? r.accountcode ?? r.code ?? '',
  recname: r.recname ?? r.nam ?? r.accountname ?? r.name ?? '',
  description: r.description ?? r.dsc ?? '',
  code_length: Number(r.code_length ?? r.codelength ?? r.len ?? 0),
  allocation_count: Number(r.allocation_count ?? r.links ?? 0),
  allocated_amount: Number(r.allocated_amount ?? r.amount ?? r.amt ?? 0),
  raw: r,
} as any);

export const normalizeAllocationLink = (r: any): IAllocationLink => ({
  id: r.id ?? r.recid ?? r.rid,
  recid: r.recid ?? r.rid ?? r.id,
  activityid: r.activityid ?? r.aci ?? null,
  activity_code: r.activity_code ?? r.activitycode ?? r.acc ?? r.acode ?? '',
  activity_name: r.activity_name ?? r.activityname ?? r.acn ?? '',
  ccid: r.ccid ?? r.costcenterid ?? null,
  costcenter_code: r.costcenter_code ?? r.costcentercode ?? r.ccc ?? '',
  costcenter_name: r.costcenter_name ?? r.costcentername ?? r.ccn ?? '',
  lineitemid: r.lineitemid ?? r.accountid ?? r.lii ?? null,
  lineitem_code: r.lineitem_code ?? r.lineitemcode ?? r.accountcode ?? r.lic ?? '',
  lineitem_name: r.lineitem_name ?? r.lineitemname ?? r.accountname ?? r.lin ?? '',
  allocationratio: Number(r.allocationratio ?? r.ratio ?? r.rto ?? 0),
  allocatedamount: Number(r.allocatedamount ?? r.allocated_amount ?? r.amount ?? r.amt ?? 0),
  status: Number(r.status ?? r.sts ?? 1),
  raw: r,
} as any);

export const normalizeAllocationIssue = (r: any): IAllocationValidationIssue => ({
  activityid: r.activityid ?? r.aci ?? null,
  activity_code: r.activity_code ?? r.activitycode ?? r.acc ?? '',
  activity_name: r.activity_name ?? r.activityname ?? r.acn ?? '',
  ccid: r.ccid ?? r.costcenterid ?? null,
  costcenter_code: r.costcenter_code ?? r.costcentercode ?? r.ccc ?? '',
  issue_code: r.issue_code ?? r.issuecode ?? r.iss ?? '',
  severity: r.severity ?? r.sev ?? 'INFO',
  message: r.message ?? r.msg ?? r.description ?? '',
  raw: r,
} as any);

export const normalizeAllocationSummary = (r: any): IAllocationSummary => ({
  costcenters: Number(r.costcenters ?? r.costcenter_count ?? 0),
  activities: Number(r.activities ?? r.activity_count ?? 0),
  allocated_activities: Number(r.allocated_activities ?? r.allocatedactivities ?? 0),
  unallocated_activities: Number(r.unallocated_activities ?? r.unallocatedactivities ?? 0),
  allocation_links: Number(r.allocation_links ?? r.links ?? 0),
  allocated_amount: Number(r.allocated_amount ?? r.amount ?? r.amt ?? 0),
});

export const fetchCostCenters = async (): Promise<IAllocationCostCenter[]> =>
  rows(await postBiz({ s: 'allocation', a: 'costcenters' })).map(normalizeAllocationCostCenter);

export const fetchActivities = async (
  costcenterid: number,
  search = ''
): Promise<IAllocationActivity[]> =>
  rows(await postBiz({ 
    s: 'allocation', 
    a: 'activities',
    cci: costcenterid || null,
    qry: search || null,
  })).map(normalizeAllocationActivity);

export const fetchLineItems = async (
  search = '',
  sixdigitOnly = true
): Promise<IAllocationLineItem[]> =>
  rows(await postBiz({ 
    s: 'allocation', 
    a: 'lineitems',
    qry: search || null,
    six: sixdigitOnly || null,
  })).map(normalizeAllocationLineItem);

export const fetchLinks = async (
  costcenterid: string | number | '',
  activityid?: string | number | ''
): Promise<IAllocationLink[]> =>
  rows(await postBiz({ 
    s: 'allocation', 
    a: 'links',
    cci: costcenterid || null,
    aci: activityid || null,
  })).map(normalizeAllocationLink);
  // rows(await postBiz(SERVICE, 'links', {
  //   cci: costcenterid || null,
  //   aci: activityid || null,
  // })).map(normalizeAllocationLink);

export const fetchSummary = async (
  costcenterid?: string | number | ''
): Promise<IAllocationSummary> =>
  normalizeAllocationSummary(json(await postBiz({ 
    s: 'allocation', 
    a: 'summary',
    cci: costcenterid || null,
  })));
  // normalizeAllocationSummary(json(await postBiz(SERVICE, 'summary', {
  //   cci: costcenterid || null,
  // })));

export const fetchValidationIssues = async (): Promise<IAllocationValidationIssue[]> =>
  rows(await postBiz({ 
    s: 'allocation', 
    a: 'validation',
  })).map(normalizeAllocationIssue);
  // rows(await postBiz(SERVICE, 'validation', {})).map(normalizeAllocationIssue);

// export const fetchAllocationAccounts = async (search = ''): Promise<IAllocationLineItem[]> =>
//   rows(await postBiz({ 
//     s: 'allocation', 
//     a: 'accounts',
//     qry: search || null,
//   })).map(normalizeAllocationLineItem);
//   // rows(await postBiz(SERVICE, 'accounts', { q: search || null })).map(normalizeAllocationLineItem);

export const processAllocation = async (payload: {
  costcenterid: string | number;
  activityids: Array<string | number>;
  lineitemids: Array<string | number>;
  mode: AllocationMode;
  totalamount?: number;
}) =>
  json(await postBiz({
    s: 'allocation', 
    a: 'summary',
    cci: payload.costcenterid,
    ais: payload.activityids, // activityids
    lis: payload.lineitemids, // lineitemids
    mod: payload.mode,
    tot: Number(payload.totalamount || 0), //amount
  }));

export const deleteLinks = async (linkids: Array<string | number>) =>
  json(await postBiz({ 
    s: 'allocation', 
    a: 'summary',
    lis: linkids 
  }));

// export const unmapAllocationLinks = async (linkids: Array<string | number>) =>
//   json(await postBiz(SERVICE, 'unmap', { linkids }));


/*
export const fetchAllocationCostCenters = async (): Promise<IAllocationCostCenter[]> =>
  rows(await postBiz({ s: 'allocation', a: 'costcenters' })).map(normalizeAllocationCostCenter);

export const fetchAllocationActivities = async (
  costcenterid: number,
  search = ''
): Promise<IAllocationActivity[]> =>
  rows(await postBiz({ 
    s: 'allocation', 
    a: 'activities',
    cci: costcenterid || null,
    qry: search || null,
  })).map(normalizeAllocationActivity);

export const fetchAllocationLineItems = async (
  search = '',
  sixdigitOnly = true
): Promise<IAllocationLineItem[]> =>
  rows(await postBiz({ 
    s: 'allocation', 
    a: 'lineitems',
    qry: search || null,
    six: sixdigitOnly || null,
  })).map(normalizeAllocationLineItem);

export const fetchAllocationLinks = async (
  costcenterid: string | number | '',
  activityid?: string | number | ''
): Promise<IAllocationLink[]> =>
  rows(await postBiz({ 
    s: 'allocation', 
    a: 'links',
    cci: costcenterid || null,
    aci: activityid || null,
  })).map(normalizeAllocationLink);
  // rows(await postBiz(SERVICE, 'links', {
  //   cci: costcenterid || null,
  //   aci: activityid || null,
  // })).map(normalizeAllocationLink);

export const fetchAllocationSummary = async (
  costcenterid?: string | number | ''
): Promise<IAllocationSummary> =>
  normalizeAllocationSummary(json(await postBiz({ 
    s: 'allocation', 
    a: 'summary',
    cci: costcenterid || null,
  })));
  // normalizeAllocationSummary(json(await postBiz(SERVICE, 'summary', {
  //   cci: costcenterid || null,
  // })));

export const fetchAllocationValidationIssues = async (): Promise<IAllocationValidationIssue[]> =>
  rows(await postBiz({ 
    s: 'allocation', 
    a: 'validation',
  })).map(normalizeAllocationIssue);
  // rows(await postBiz(SERVICE, 'validation', {})).map(normalizeAllocationIssue);

// export const fetchAllocationAccounts = async (search = ''): Promise<IAllocationLineItem[]> =>
//   rows(await postBiz({ 
//     s: 'allocation', 
//     a: 'accounts',
//     qry: search || null,
//   })).map(normalizeAllocationLineItem);
//   // rows(await postBiz(SERVICE, 'accounts', { q: search || null })).map(normalizeAllocationLineItem);

export const processAllocation = async (payload: {
  costcenterid: string | number;
  activityids: Array<string | number>;
  lineitemids: Array<string | number>;
  mode: AllocationMode;
  totalamount?: number;
}) =>
  json(await postBiz({
    s: 'allocation', 
    a: 'summary',
    cci: payload.costcenterid,
    ais: payload.activityids, // activityids
    lis: payload.lineitemids, // lineitemids
    mod: payload.mode,
    tot: Number(payload.totalamount || 0), //amount
  }));

export const deleteAllocationLinks = async (linkids: Array<string | number>) =>
  json(await postBiz({ 
    s: 'allocation', 
    a: 'summary',
    lis: linkids 
  }));

  */